
// cps.assets.resolve(assetId, "LowResModel") --> outputs "/assets/obstructions/bag-2.glb"

type ResolveFunction = (assetId: string, type: string) => string

class AssetPathResolver {
    match: RegExp;

    // Larger priority numbers are resolved first
    priority: number;
    resolve: ResolveFunction;

    constructor(match: RegExp, priority: number, resolve: ResolveFunction) {
        this.match = match;
        this.priority = priority;
        this.resolve = resolve;
    }
}

export class AssetPathResolverError extends Error { }

export class AssetManager {
    private resolvers: AssetPathResolver[] = [];

    addResolver(match: RegExp, priority: number, resolve: ResolveFunction) {
        this.resolvers.push(new AssetPathResolver(match, priority, resolve));
        this.resolvers.sort((a, b) => b.priority - a.priority);
    }

    resolve(assetId: string, type: string): string {
        for(const resolver of this.resolvers) {
            if(resolver.match.test(assetId)) {
                return resolver.resolve(assetId, type);
            }
        }
        throw new AssetPathResolverError(`No resolver for assetId "${assetId}"`);
    };
}

const assets = new AssetManager();

// Per-client files exactly mirror the hierarchy of regular assets, but are
// rooted at /company-files/[client name]/assets/...
// We allways match client assets first so they may shadow built-in assets
assets.addResolver(
    /^clients\/[^\/]+\/.+/, Infinity,
    (assetId: string, type: string): string => {
        const [_, client, suffix] = /^clients\/([^\/]+)\/(.+)/.exec(assetId);
        // assetPath will begin with `/assets/`
        const assetPath = assets.resolve(suffix, type);
        return `/company-files/${client}${assetPath}`;
    });

assets.addResolver(
    /^obstructions\//, 10,
    (assetId: string, type: string): string => {
        switch(type) {
            case 'Preview':
                return `/assets/${assetId}-preview.png`;
            case 'LowResModel':
                return `/assets/${assetId}.glb`;
            case 'HighResModel':
                return `/assets/${assetId}.blend`;
        }
        throw new AssetPathResolverError(`Unsupported feature type "${type}"`);
    });

assets.addResolver(
    /^materials\//, 20,
    (assetId: string, type: string): string => {
        switch(type) {
            case 'OnlineMaterial':
                return `/assets/${assetId}.glb`;
        }
        throw new AssetPathResolverError(`Unsupported feature type "${type}"`);
    });

// Catch-all resolver will just use the asset ID as a file path and hope for
// the best. It is the last to match, with a negative infinite priority so it's
// always last in the list. It just assumes you're looking for a glb/blend
// combo. This way we don't have to add resolvers unless they offer files more
// than just glb and blend.
assets.addResolver(
    /.*/, -Infinity,
    (assetId: string, type: string): string => {
        switch(type) {
            case 'LowResModel':
                return `/assets/${assetId}.glb`;
            case 'HighResModel':
                return `/assets/${assetId}.blend`;
        }
        throw new AssetPathResolverError(`Unsupported feature type "${type}"`);
    });

// We'd have to make the assets.resolve() method async, but we could
// conceivably resolve assets on the server, e.g. client-contributed assets
// that live in the database, but I think it would be easier to do client
// assets as above.
//
// assets.addResolver(
//     /.*/, 9999,
//     (assetId: string, type: string): string => {
//         const response = await fetch(`/api/assets/${assetId}?type=${type}`);
//         // etc...
//     });

export default assets;
